import { useCallback, useRef, useState } from "react";

import { Button } from "@mui/material";
import axios, { AxiosResponse } from "axios";
import { useSnackbar } from "notistack";

import { axiosXero } from "../../api";
import { toXeroPushResult, xeroPushBody } from "../pushContract";
import { XERO_PUSH_CHUNK, xeroPushNoun } from "../types";
import { useXeroConnected } from "./useXeroConnected";

import type { XeroConnection } from "./useXeroConnected";

import type { IXeroPushOutcome, IXeroPushRow, XeroPushKind } from "../types";

const chunk = (ids: number[], size: number): number[][] => {
  const groups: number[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    groups.push(ids.slice(i, i + size));
  }
  return groups;
};

/** The service deletes the credential row and answers this when the token is dead. */
const isReconnectError = (e: unknown) =>
  axios.isAxiosError(e) &&
  e.response?.status === 403 &&
  (e.response?.data as { error?: string })?.error === "reconnect";

/**
 * The reason the service gave for refusing. The upstream service says it in
 * `error` on a 400; FastAPI says it in `detail` on every refusal (404 no such
 * record, 409 duplicate bill, 502 Xero's own reason). Not connected is a 403
 * `error: "reconnect"` from both, which isReconnectError handles first.
 */
const serviceReason = (e: unknown) => {
  if (!axios.isAxiosError(e)) {
    return undefined;
  }
  const data = e.response?.data as { error?: string; detail?: unknown } | undefined;
  if (e.response?.status === 400 && data?.error) {
    return data.error;
  }
  return typeof data?.detail === "string" ? data.detail : undefined;
};

/**
 * A skipped record was already in Xero, so it is neither pushed nor a failure;
 * it still answers ok.
 */
const summarise = (
  kind: XeroPushKind,
  rows: IXeroPushRow[],
  batches: string[],
  requested: number
): IXeroPushOutcome => {
  const skipped = rows.filter((row) => row.skipped).length;
  const failed = rows.filter((row) => !row.ok && !row.skipped).length;
  return {
    kind,
    batches,
    requested,
    results: rows,
    ok: rows.length - skipped - failed,
    failed,
    skipped,
  };
};

/**
 * A push while the results drawer is open is a retry of what it is showing, so
 * the drawer takes the new answers: rows the retry covered are replaced, rows
 * it did not touch stay, and the header, the failures and the Retry button all
 * describe where the records stand now.
 */
const mergeOutcomes = (
  prev: IXeroPushOutcome,
  next: IXeroPushOutcome
): IXeroPushOutcome => {
  if (prev.kind !== next.kind) {
    return next;
  }
  const answered = new Map(next.results.map((row) => [row.id, row]));
  const seen = new Set(prev.results.map((row) => row.id));
  const rows = prev.results
    .map((row) => answered.get(row.id) ?? row)
    .concat(next.results.filter((row) => !seen.has(row.id)));
  return summarise(
    next.kind,
    rows,
    [...prev.batches, ...next.batches.filter((batch) => !prev.batches.includes(batch))],
    // a retry answers for ids the drawer already listed, so what the user asked
    // for is still the wider of the two
    Math.max(prev.requested, rows.length)
  );
};

/**
 * User-clicked push of Aeronet records to Xero. Without a connection there is
 * nothing to push to, so the hook swallows the click and raises
 * `connectRequired` instead; the caller renders XeroConnectRequiredDialog off it.
 *
 * `enabled` is the Xero module gate: false means no GET /state at all.
 *
 * A page that reads the connection state itself passes it in: two instances
 * would mean two GET /state on load and two copies of `connected`, so a
 * disconnect on the page would leave the push hook still believing in the row
 * it just deleted.
 */
export function useXeroPush(enabled = true, connection?: XeroConnection) {
  // hooks cannot be called conditionally, so the hook's own state is read with
  // the request disabled when a caller supplied one
  const own = useXeroConnected(enabled && connection === undefined);
  const { usable, wrongOrg, email, refresh } = connection ?? own;
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [outcome, setOutcome] = useState<IXeroPushOutcome>();
  const [connectRequired, setConnectRequired] = useState(false);
  // `loading` is a render behind the click, so it cannot gate a second push:
  // two clicks in the same tick both see `false`. The ref is set before the
  // first await and is what the second click reads.
  const inFlight = useRef(false);

  const push = useCallback(
    async (
      kind: XeroPushKind,
      ids: number[]
    ): Promise<IXeroPushOutcome | undefined> => {
      // Not usable covers four states: no row, a state that has not answered
      // yet, a dead grant and a row pinned to another Xero organisation. Every
      // one of them 403s the push, so none of them sends it; the user retries
      // once the dialog is dismissed.
      if (!usable) {
        setConnectRequired(true);
        return undefined;
      }
      if (ids.length === 0) {
        enqueueSnackbar("Select a row to continue", { variant: "warning" });
        return undefined;
      }
      // A second push while one is in flight would open a second batch for the
      // same records; the service skips the already-exported ones, but the two
      // answers would then race for the outcome drawer.
      if (inFlight.current) {
        return undefined;
      }
      inFlight.current = true;
      setLoading(true);
      try {
        // Chunks go up sequentially: the service rejects a request over its
        // per-kind cap, and each chunk opens its own batch. Chunks are in id
        // order and the service answers one row per requested id in request
        // order, so concatenating the answers keeps the merged rows in id order.
        const rows: IXeroPushRow[] = [];
        const batches: string[] = [];
        let failure: unknown;
        for (const group of chunk(ids, XERO_PUSH_CHUNK[kind])) {
          try {
            const resp: AxiosResponse<unknown> = await axiosXero.post(
              `/${kind}/push`,
              xeroPushBody(kind, group),
              // Every failure is reported below, so the global toast would say
              // it twice. It also means a push failure can never sign the user
              // out: until Aeronet.app 46f8a2ac, FastAPI answered Xero failures
              // with 401, and the interceptor drops the session on any 401.
              { suppressErrorToast: true }
            );
            const answer = toXeroPushResult(kind, group, resp.data);
            rows.push(...answer.results);
            if (answer.batch_id) {
              batches.push(answer.batch_id);
            }
          } catch (e) {
            // Stop, but keep what the earlier chunks pushed: those records and
            // their batches are in Xero already, and reporting a bare failure
            // would send the user off to push them a second time.
            failure = e;
            break;
          }
        }

        if (failure !== undefined) {
          if (isReconnectError(failure)) {
            // the credential row is gone: re-read the state and send the user
            // back to Settings instead of reporting a generic failure
            refresh();
            setConnectRequired(true);
          } else {
            enqueueSnackbar(
              serviceReason(failure) ?? "Error pushing to Xero",
              { variant: "error" }
            );
          }
          if (rows.length === 0) {
            return undefined;
          }
          // the drawer is the only place the user can see which records made it
          const partial = summarise(kind, rows, batches, ids.length);
          setOutcome((prev) => (prev ? mergeOutcomes(prev, partial) : partial));
          return partial;
        }

        const merged = summarise(kind, rows, batches, ids.length);
        const { failed, skipped } = merged;
        const total = rows.length;

        // An open drawer is showing the push this one retried, so it takes the
        // new answers whatever they are. A closed drawer stays closed: only the
        // snackbar's Details opens one.
        setOutcome((prev) => (prev ? mergeOutcomes(prev, merged) : prev));

        if (failed > 0) {
          const batch = batches.join(", ");
          enqueueSnackbar(
            `Pushed ${merged.ok} of ${total} ${xeroPushNoun(kind, total)} to Xero${
              batch ? `, batch ${batch}` : ""
            } · ${failed} failed`,
            {
              variant: "warning",
              // the per-record reasons are too long for a snackbar, so the user
              // opts into the drawer
              action: (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    setOutcome((prev) =>
                      prev ? mergeOutcomes(prev, merged) : merged
                    )
                  }
                >
                  Details
                </Button>
              ),
            }
          );
        } else if (skipped === total) {
          enqueueSnackbar(`${total} ${xeroPushNoun(kind, total)} already in Xero`, {
            variant: "info",
          });
        } else if (skipped > 0) {
          // some were already in Xero: "Pushed 5 invoices" would claim more
          // work than the push did
          enqueueSnackbar(
            `Pushed ${merged.ok} of ${total} ${xeroPushNoun(kind, total)} to Xero`,
            { variant: "success" }
          );
        } else {
          enqueueSnackbar(`Pushed ${total} ${xeroPushNoun(kind, total)} to Xero`, {
            variant: "success",
          });
        }
        return merged;
      } catch (e) {
        // every request failure is handled per chunk above, so this is a bug
        console.error("Xero push failed: ", e);
        enqueueSnackbar("Error pushing to Xero", { variant: "error" });
        return undefined;
      } finally {
        inFlight.current = false;
        setLoading(false);
      }
    },
    [usable, enqueueSnackbar, refresh]
  );

  const clearOutcome = useCallback(() => setOutcome(undefined), []);
  const closeConnectRequired = useCallback(() => setConnectRequired(false), []);

  return {
    push,
    loading,
    outcome,
    clearOutcome,
    connectRequired,
    closeConnectRequired,
    /** The connection is pinned to another Xero org: the dialog says so. */
    wrongOrg,
    /** The connected user's Xero email; the drawer credits the push to it. */
    email,
  };
}
